import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  fetchTeamById,
  withdrawTeam,
  deleteTeam,
  transferCaptaincy,
  clearTeamError,
} from '../../store/slices/teamSlice';
import { withdrawEnrollment, enrollTeam } from '../../store/slices/enrollmentSlice';
import { fetchLeague, fetchSeasons, fetchDivisionsForSeason } from '../../store/slices/leagueSlice';
import { resolveEnrollmentSeason } from '../../utils/season';
import {
  TeamStatus,
  TeamType,
  TeamMemberRole,
  PartnershipStatus,
  EnrollmentStatus,
  Gender,
  UserRole,
} from '@sulis-liiga/common';
import type { TeamMember, Enrollment } from '@sulis-liiga/common';
import teamService from '../../services/teamService';
import type { TeamPlayerProfile } from '../../services/teamService';
import { getTeamLifecycle } from '../../utils/teamLifecycle';
import { groupEnrollmentsByLadder } from '../../utils/enrollmentGrouping';
import { enrollmentStatusLabel, enrollmentStatusClassName } from '../../utils/enrollmentStatus';
import EnrollDivisionPicker from '../../components/team/EnrollDivisionPicker';
import Modal from '../../components/common/Modal';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TEAM_TYPE_LABELS: Record<TeamType, string> = {
  [TeamType.SINGLES]: 'Singles',
  [TeamType.DOUBLES]: 'Doubles',
  [TeamType.MIXED_DOUBLES]: 'Mixed Doubles',
};

function getPartnershipBadge(status?: PartnershipStatus): { label: string; className: string } {
  switch (status) {
    case PartnershipStatus.ACCEPTED:
      return { label: 'Accepted', className: 'bg-success-100 text-success-800' };
    case PartnershipStatus.PENDING:
      return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' };
    case PartnershipStatus.DECLINED:
      return { label: 'Declined', className: 'bg-red-100 text-red-700' };
    default:
      return { label: 'Member', className: 'bg-gray-100 text-gray-600' };
  }
}

/** Two-letter initials from a display name (falls back to the first 2 chars). */
function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <svg
        className="animate-spin h-8 w-8 text-primary-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-label="Loading"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
}

// ─── TransferCaptaincyModal ───────────────────────────────────────────────────

interface TransferModalProps {
  members: TeamMember[];
  currentCaptainId: string;
  nameFor: (userId: string) => string;
  onConfirm: (userId: string) => void;
  onClose: () => void;
  loading: boolean;
}

const TransferCaptaincyModal: React.FC<TransferModalProps> = ({
  members,
  currentCaptainId,
  nameFor,
  onConfirm,
  onClose,
  loading,
}) => {
  const [selected, setSelected] = useState<string>('');
  const acceptedMembers = members.filter(
    m => m.userId !== currentCaptainId && m.partnershipStatus === PartnershipStatus.ACCEPTED,
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
        <h3 id="transfer-modal-title" className="text-lg font-semibold text-gray-900 mb-3">
          Transfer captaincy
        </h3>
        {acceptedMembers.length === 0 ? (
          <p className="text-sm text-gray-500">No accepted members to transfer captaincy to.</p>
        ) : (
          <div className="space-y-2">
            {acceptedMembers.map(m => (
              <label key={m.userId} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="new-captain"
                  value={m.userId}
                  checked={selected === m.userId}
                  onChange={() => setSelected(m.userId)}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="h-7 w-7 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-medium">
                  {initialsFromName(nameFor(m.userId))}
                </span>
                <span className="text-sm font-medium text-gray-700">{nameFor(m.userId)}</span>
              </label>
            ))}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => selected && onConfirm(selected)}
            disabled={!selected || loading}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const TeamDetailPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();

  const { currentTeam: team, loading, error } = useSelector((state: RootState) => state.team);
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { selectedLeagueId } = useSelector((state: RootState) => state.ui);
  const { seasons, divisions, currentLeague } = useSelector((state: RootState) => state.league);

  // Enroll into the active season, or the earliest upcoming one when only next
  // season's (still-UPCOMING) divisions have been set up.
  const enrollSeason = useMemo(() => resolveEnrollmentSeason(seasons), [seasons]);

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [withdrawConfirm, setWithdrawConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [playerProfiles, setPlayerProfiles] = useState<Record<string, TeamPlayerProfile>>({});
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [enrollGroupIds, setEnrollGroupIds] = useState<string[]>([]);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollNotice, setEnrollNotice] = useState<string | null>(null);
  // The per-division enrollment awaiting a withdraw confirmation (null = no
  // dialog open). A misclick on a division's "Withdraw" must not withdraw it
  // outright — the captain confirms in this dialog first.
  const [enrollmentToWithdraw, setEnrollmentToWithdraw] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [withdrawingEnrollment, setWithdrawingEnrollment] = useState(false);

  useEffect(() => {
    if (teamId) {
      dispatch(fetchTeamById(teamId));
    }
    return () => {
      dispatch(clearTeamError());
    };
  }, [dispatch, teamId]);

  // Members are stored on the team only as user ids; resolve them to registry
  // profiles so we can show real names. We never render the raw id — until the
  // profiles load (or if one can't be resolved) we show a neutral placeholder.
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    setProfilesLoaded(false);
    teamService
      .getTeamWithPlayers(teamId)
      .then(({ players }) => {
        if (cancelled) return;
        const map: Record<string, TeamPlayerProfile> = {};
        players.forEach(p => {
          map[p.id] = p;
        });
        setPlayerProfiles(map);
      })
      .catch(() => {
        /* names are best-effort; show a placeholder rather than the id */
      })
      .finally(() => {
        if (!cancelled) setProfilesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  // Load the active league's divisions so a captain can enroll the team into
  // further ladders directly from here (the re-enroll path now that Join League
  // is gone).
  useEffect(() => {
    if (selectedLeagueId) {
      dispatch(fetchLeague(selectedLeagueId));
      dispatch(fetchSeasons(selectedLeagueId));
    }
  }, [dispatch, selectedLeagueId]);

  useEffect(() => {
    if (selectedLeagueId && enrollSeason?.id) {
      dispatch(fetchDivisionsForSeason({ leagueId: selectedLeagueId, seasonId: enrollSeason.id }));
    }
  }, [dispatch, selectedLeagueId, enrollSeason?.id]);

  // Resolve a member's display name from the fetched registry profiles. Never
  // returns a raw id: a placeholder is shown while loading or if unresolved.
  const nameFor = (userId: string): string => {
    const p = playerProfiles[userId];
    if (p) return `${p.firstName} ${p.lastName}`.trim();
    return profilesLoaded ? 'Unknown player' : 'Loading…';
  };

  if (loading && !team) {
    return <Spinner />;
  }

  if (!team && !loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Team not found.</p>
        <Link to="/teams" className="mt-2 text-sm text-primary-600 hover:underline">
          Back to Teams
        </Link>
      </div>
    );
  }

  if (!team) return <Spinner />;

  const statusBadge = getTeamLifecycle(team);
  const isCaptain = isAuthenticated && user?.id === team.captainId;
  // Any accepted member can manage the team (enroll / withdraw / propose) — the
  // captain and the accepted partner see the same card; only Transfer Captaincy
  // stays captain-only.
  const isAcceptedMember =
    isAuthenticated &&
    !!user &&
    team.players.some(
      p =>
        p.userId === user.id &&
        (p.partnershipStatus === undefined || p.partnershipStatus === PartnershipStatus.ACCEPTED),
    );
  const canManage =
    (isCaptain || isAcceptedMember) &&
    team.status !== TeamStatus.WITHDRAWN &&
    team.status !== TeamStatus.CANCELLED;
  const canWithdraw = canManage;
  // Enrollment is also offered on a WITHDRAWN team: re-enrolling reactivates it
  // (backend flips it back to ACTIVE), so a captain can bring the team back for a
  // coming season without recreating it. Only cancelled (partner declined) and
  // deleted teams stay non-enrollable.
  const isWithdrawn = team.status === TeamStatus.WITHDRAWN;
  const canEnroll =
    (isCaptain || isAcceptedMember) &&
    team.status !== TeamStatus.CANCELLED &&
    team.status !== TeamStatus.DELETED;
  // Permanent deletion is limited to the team's creator (captain) or a Super
  // Administrator, and is offered regardless of the withdrawn/cancelled state.
  const isSuperAdmin =
    isAuthenticated && !!user && (user.roles?.includes(UserRole.SUPER_ADMINISTRATOR) ?? false);
  const canDelete = isCaptain || isSuperAdmin;

  // Division eligibility is size + gender rule, so pass the team's player genders.
  const teamGenders: Gender[] = team.players.map(
    p => (playerProfiles[p.userId]?.gender as Gender) ?? Gender.MALE,
  );
  const enrolledGroupIds = (team.enrollments ?? [])
    .filter(e => e.status !== EnrollmentStatus.WITHDRAWN)
    .map(e => e.divisionGroupId);

  const handleEnroll = async () => {
    if (!selectedLeagueId || enrollGroupIds.length === 0) return;
    setEnrolling(true);
    setEnrollNotice(null);
    // seasonId is intentionally omitted — the backend targets the upcoming
    // season's roster, or queues the team (season-less) when none exists yet.
    const results = await Promise.allSettled(
      enrollGroupIds.map(groupId =>
        dispatch(
          enrollTeam({
            teamId: team.id,
            req: { leagueId: selectedLeagueId, divisionGroupId: groupId },
          }),
        ).unwrap(),
      ),
    );
    const anyQueued = results.some(
      r => r.status === 'fulfilled' && (r.value as Enrollment).status === EnrollmentStatus.QUEUED,
    );
    if (anyQueued) {
      setEnrollNotice(
        'Queued for the next season — there is no upcoming season yet, so the team joins the roster automatically once one is created.',
      );
    }
    setEnrollGroupIds([]);
    setEnrolling(false);
    dispatch(fetchTeamById(team.id));
  };

  const handleWithdraw = () => {
    if (!withdrawConfirm) {
      setWithdrawConfirm(true);
      return;
    }
    dispatch(withdrawTeam(team.id)).then(() => {
      setWithdrawConfirm(false);
      navigate('/teams');
    });
  };

  const handleDelete = () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    dispatch(deleteTeam(team.id)).then(() => {
      setDeleteConfirm(false);
      navigate('/teams');
    });
  };

  const handleTransferConfirm = (newCaptainUserId: string) => {
    dispatch(transferCaptaincy({ teamId: team.id, newCaptainUserId })).then(() => {
      setShowTransferModal(false);
    });
  };

  // Withdraw a single division enrollment, only after the captain confirms in the
  // dialog — guards against a misclick withdrawing the team from a division.
  const confirmWithdrawEnrollment = async () => {
    if (!enrollmentToWithdraw) return;
    setWithdrawingEnrollment(true);
    await dispatch(withdrawEnrollment(enrollmentToWithdraw.id))
      .unwrap()
      .then(() => dispatch(fetchTeamById(team.id)))
      .catch(() => {
        /* the enrollment slice surfaces the error; nothing further to do here */
      });
    setWithdrawingEnrollment(false);
    setEnrollmentToWithdraw(null);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/teams" className="text-sm text-primary-600 hover:underline">
        &larr; Back to Teams
      </Link>

      {/* Error */}
      {error && (
        <div className="mt-3 rounded-md bg-danger-50 p-3" role="alert">
          <p className="text-sm text-danger-800">{error}</p>
        </div>
      )}

      {/* ── Team header ───────────────────────────────────────────────────── */}
      <section className="mt-2 bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{team.name}</h1>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                {TEAM_TYPE_LABELS[team.type]}
              </span>
              <span className={`px-2 py-0.5 rounded-full font-medium ${statusBadge.className}`}>
                {statusBadge.label}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Members ───────────────────────────────────────────────────────── */}
      <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-sm font-semibold text-gray-700">Members</h2>
        <ul className="mt-3 divide-y divide-gray-100" aria-label="Team members">
          {team.players.map((member, idx) => {
            const isMemberCaptain =
              member.userId === team.captainId || member.role === TeamMemberRole.CAPTAIN;
            const partnerBadge = isMemberCaptain
              ? null
              : getPartnershipBadge(member.partnershipStatus);

            return (
              <li
                key={`${member.userId}-${idx}`}
                className="py-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-sm font-semibold">
                    {initialsFromName(nameFor(member.userId))}
                  </span>
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      <span>{nameFor(member.userId)}</span>
                      {isMemberCaptain ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary-600 text-white">
                          &#9733; Captain
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          Member
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {partnerBadge && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${partnerBadge.className}`}>
                    {partnerBadge.label}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        <p className="mt-2 text-xs text-gray-400">
          The captain manages the team — enrolling it into a division, proposing games, and
          submitting results.
        </p>
      </section>

      {/* ── Enrollments (one row per division ladder, across seasons) ──────── */}
      {team.enrollments && team.enrollments.length > 0 && (
        <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">
            Enrollments
            {currentLeague?.name && (
              <span className="text-gray-400 font-normal"> &middot; {currentLeague.name}</span>
            )}
          </h2>
          <ul className="mt-3 divide-y divide-gray-100">
            {groupEnrollmentsByLadder(team.enrollments).map(enrollment => {
              const enrolledSinceSeasonName = seasons.find(
                s => s.id === enrollment.enrolledSinceSeasonId,
              )?.name;
              return (
                <li key={enrollment.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {enrollment.divisionBaseName ?? enrollment.divisionGroupId}
                      {enrollment.divisionName ? (
                        <span className="text-gray-500 font-normal">
                          {' '}
                          &middot; {enrollment.divisionName}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {enrollment.category}
                      {' · '}
                      <span className={enrollmentStatusClassName(enrollment.status)}>
                        {enrollmentStatusLabel(enrollment.status)}
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {enrolledSinceSeasonName
                        ? `Enrolled since ${enrolledSinceSeasonName}`
                        : 'Queued (not yet in a season)'}
                    </p>
                    {enrollment.status === EnrollmentStatus.ACTIVE && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {enrollment.matches.played} played &middot; {enrollment.matches.won}W{' '}
                        {enrollment.matches.lost}L &middot; {enrollment.points} pts
                      </p>
                    )}
                  </div>
                  {canManage && enrollment.status !== EnrollmentStatus.WITHDRAWN && (
                    <button
                      type="button"
                      onClick={() =>
                        setEnrollmentToWithdraw({
                          id: enrollment.id,
                          label: enrollment.divisionBaseName ?? enrollment.divisionGroupId,
                        })
                      }
                      className="text-xs px-2 py-1 rounded-md text-danger-600 border border-danger-300 hover:bg-danger-50"
                    >
                      Withdraw
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* ── Enroll in a division (captain) ────────────────────────────────── */}
      {canEnroll && (
        <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">
            {isWithdrawn ? 'Re-enroll in a division' : 'Enroll in a division'}
          </h2>
          <p className="mt-1 text-xs text-gray-400">
            Choose one or more eligible ladders to enter. An admin places your team into a tier
            after enrollment.
          </p>
          {isWithdrawn && (
            <p className="mt-2 text-xs text-blue-700" role="status">
              This team is withdrawn. Enrolling it into a division reactivates the team and adds it
              to the coming season — an active season is closed to new entries.
            </p>
          )}
          <EnrollDivisionPicker
            divisions={divisions}
            genders={teamGenders}
            selected={enrollGroupIds}
            onChange={setEnrollGroupIds}
            excludeGroupIds={enrolledGroupIds}
          />
          <div className="mt-4">
            <button
              type="button"
              onClick={handleEnroll}
              disabled={enrollGroupIds.length === 0 || enrolling}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {enrolling ? 'Enrolling…' : 'Enroll team'}
            </button>
            {enrollNotice && (
              <p className="mt-2 text-xs text-blue-700" role="status">
                {enrollNotice}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ── Team management (any accepted member; delete for creator/admin) ── */}
      {(canManage || canDelete) && (
        <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Manage team</h2>
            <span className="text-xs text-gray-400">
              Visible to team members and administrators
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-3">
            {canManage && (
              <Link
                to="/games/proposals/create"
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                Propose a game
              </Link>
            )}

            {/* Transfer captaincy is the only captain-only action. */}
            {isCaptain &&
              team.type !== TeamType.SINGLES &&
              team.players.some(
                m =>
                  m.userId !== team.captainId && m.partnershipStatus === PartnershipStatus.ACCEPTED,
              ) && (
                <button
                  type="button"
                  onClick={() => setShowTransferModal(true)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-primary-600 border border-primary-600 hover:bg-primary-50"
                >
                  Transfer captaincy
                </button>
              )}

            {canWithdraw && (
              <button
                type="button"
                onClick={handleWithdraw}
                disabled={loading}
                className="px-4 py-2 rounded-md text-sm font-medium text-danger-600 border border-danger-600 hover:bg-danger-50 disabled:opacity-50"
                aria-label={withdrawConfirm ? 'Confirm withdrawal' : 'Withdraw team'}
              >
                {withdrawConfirm ? 'Confirm withdrawal' : 'Withdraw team'}
              </button>
            )}
            {withdrawConfirm && (
              <button
                type="button"
                onClick={() => setWithdrawConfirm(false)}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}

            {/* Permanent delete (creator or Super Admin). Distinct from Withdraw:
                a team with no history is removed entirely, one with games/enrollments
                is archived with its historical name preserved. */}
            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-danger-600 hover:bg-danger-700 disabled:opacity-50"
                aria-label={deleteConfirm ? 'Confirm permanent deletion' : 'Delete team'}
              >
                {deleteConfirm ? 'Confirm delete — permanent' : 'Delete team'}
              </button>
            )}
            {deleteConfirm && (
              <button
                type="button"
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
          {deleteConfirm && (
            <p className="mt-3 text-xs text-danger-700">
              Deleting permanently removes this team. If it has played games or been enrolled, its
              records are kept but its name is preserved for historical standings. This cannot be
              undone.
            </p>
          )}
        </section>
      )}

      {/* Per-division withdraw confirmation — guards against a misclick pulling
          the team out of a division. */}
      <Modal
        isOpen={enrollmentToWithdraw !== null}
        onClose={() => {
          if (!withdrawingEnrollment) setEnrollmentToWithdraw(null);
        }}
        title="Withdraw from division?"
      >
        <p className="text-sm text-gray-600">
          Withdraw <span className="font-medium text-gray-900">{team.name}</span> from{' '}
          <span className="font-medium text-gray-900">{enrollmentToWithdraw?.label}</span>? The team
          leaves this division and its unplayed games there are removed. You can re-enroll it later.
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setEnrollmentToWithdraw(null)}
            disabled={withdrawingEnrollment}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmWithdrawEnrollment}
            disabled={withdrawingEnrollment}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-danger-600 hover:bg-danger-700 disabled:opacity-50"
          >
            {withdrawingEnrollment ? 'Withdrawing…' : 'Withdraw'}
          </button>
        </div>
      </Modal>

      {/* Transfer modal */}
      {showTransferModal && team && (
        <TransferCaptaincyModal
          members={team.players}
          currentCaptainId={team.captainId}
          nameFor={nameFor}
          onConfirm={handleTransferConfirm}
          onClose={() => setShowTransferModal(false)}
          loading={loading}
        />
      )}
    </div>
  );
};

export default TeamDetailPage;
